import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import {
  FormLabel,
  Input,
  RhinoSwitch,
} from 'rhinostyle';

import VariableMessage from './VariableMessage';
import { AUTOMATED_MESSAGE_EDIT, AUTOMATED_MESSAGE_DELETE } from '../constants/UserPermissionsConstants';
import { ValidationService, ValidationShapers } from '../services/ValidationService';
import { DateHelpers, ValidationHelpers } from '../helpers';
import { processCampaignAttachments } from '../helpers/AttachmentHelpers';
import {
  PRESCRIPTION_MESSAGE_VARIABLE_OPTIONS,
  PRESCRIPTION_MESSAGE_TEMPLATE,
} from '../constants/VariableMessageConstants';
import { PRESCRIPTION_FILE_UPLOAD_INFO } from '../constants/AppConstants';
import {
  TYPE_PRESCRIPTION_EVENT_REMINDER,
} from '../constants/Types';
import BackButton from './BackButton';
import PageLoader from './PageLoader';
import FormActionButtons from './FormActionButtons';
import {
  useFetchPrescriptionCampaign,
} from '../hooks';
import {
  useDeletePrescriptionCampaignMutation,
  useUpdatePrescriptionCampaignMutation,
  useCreatePrescriptionCampaignMutation,
} from '../services/prescriptionService';
import NotificationService from '../services/NotificationService';
import PayorsSearchSelect from './PayorsSearchSelect';
import { fetchOffices } from '../reducers/officeReducer';
import { fetchFormTemplates } from '../reducers/formReducer';
import { getCurrentOrg } from '../selectors/organizationSelectors';

const PrescriptionCampaignForm = () => {
  const history = useHistory();
  const params = useParams();
  const pageContainer = useRef();
  const dispatch = useDispatch();
  const [createCampaign] = useCreatePrescriptionCampaignMutation();
  const [updateCampaign] = useUpdatePrescriptionCampaignMutation();
  const [deleteCampaign] = useDeletePrescriptionCampaignMutation();
  const { isLoading, data: prescriptionCampaign } = useFetchPrescriptionCampaign();
  const organizationId = useSelector((state) => state.auth.currentOrg);
  const isRhinoformEnabled = useSelector((state) => !!state.form.org?.isRhinoformEnabled);
  const currentOrganization = useSelector((state) => getCurrentOrg(state));

  const { prescriptionCampaignId } = params;
  const defaultCampaign = {
    attachments: prescriptionCampaign?.attachments || [],
    default: !!prescriptionCampaign?.default || false,
    enabled: !!prescriptionCampaign?.enabled || true,
    name: prescriptionCampaign?.name || '',
    payors: prescriptionCampaign?.payors?.map(((payor) => payor.id)) || [],
    payorSearch: '',
    resendIntervalHours: `${prescriptionCampaign?.resendIntervalHours || ''}`,
    refillDeliveryHours: `${prescriptionCampaign?.refillDeliveryHours || ''}`,
    refillTemplate: prescriptionCampaign?.refillTemplate || PRESCRIPTION_MESSAGE_TEMPLATE,
    refillTemplateEnabled: !!prescriptionCampaign?.refillTemplateEnabled || false,
    template: prescriptionCampaign?.template || PRESCRIPTION_MESSAGE_TEMPLATE,
  };

  const [formInputs, setFormInputs] = useState(defaultCampaign);
  const [errors, setErrors] = useState({});
  const [formInProgress, setFormInProgress] = useState(false);

  useEffect(() => {
    if (isRhinoformEnabled) {
      dispatch(fetchFormTemplates());
    }
    if (currentOrganization?.isOfficeVCardEnabled) {
      dispatch(fetchOffices());
    }
  }, []);

  useEffect(() => {
    if (!isLoading && prescriptionCampaign) {
      setFormInputs({
        attachments: prescriptionCampaign?.attachments || [],
        default: !!prescriptionCampaign?.default || false,
        enabled: !!prescriptionCampaign?.enabled,
        initialRefillValue: prescriptionCampaign?.refillTemplate || PRESCRIPTION_MESSAGE_TEMPLATE,
        initialValue: prescriptionCampaign?.template || PRESCRIPTION_MESSAGE_TEMPLATE,
        name: prescriptionCampaign?.name || '',
        payors: prescriptionCampaign?.payors?.map(((payor) => payor.id)) || [],
        payorSearch: '',
        resendIntervalHours: `${prescriptionCampaign?.resendIntervalHours || ''}`,
        refillDeliveryHours: `${prescriptionCampaign?.refillDeliveryHours || ''}`,
        refillTemplate: prescriptionCampaign?.refillTemplate || PRESCRIPTION_MESSAGE_TEMPLATE,
        refillTemplateEnabled: !!prescriptionCampaign?.refillTemplateEnabled || false,
        template: prescriptionCampaign?.template || PRESCRIPTION_MESSAGE_TEMPLATE,
      });
    }
  }, [prescriptionCampaign, isLoading]);

  const handleChange = (fieldName, value) => {
    if (fieldName === 'attachments') {
      const { attachments } = formInputs;
      const newAttachments = value.filter((v) => {
        const existingAttachment = attachments.find((a) => (a.attachmentUrl === v.attachmentUrl && a.appointmentEventTypeId === v.appointmentEventTypeId));
        return !existingAttachment;
      });
      if (newAttachments.length === 0) {
        setFormInputs((current) => ({ ...current, attachments: value }));
      } else {
        setFormInputs((current) => ({ ...current, attachments: [...attachments, ...newAttachments] }));
      }
    } else {
      setFormInputs((current) => ({ ...current, [fieldName]: value }));
    }
  };

  const handleSubmit = async () => {
    setErrors({});
    const saveErrors = ValidationService(ValidationShapers.shapePrescriptionMessage({ ...formInputs }));
    const errorCount = Object.keys(saveErrors).length;
    if (errorCount > 0) {
      setErrors(saveErrors);
      ValidationHelpers.handleValidationErrors(errors, pageContainer.current);
    } else {
      setFormInProgress(true);
      const payload = {
        ...prescriptionCampaignId && {
          id: Number(prescriptionCampaignId),
        },
        ...formInputs,
        attachments: processCampaignAttachments(formInputs.attachments),
      };
      const response = prescriptionCampaignId ?
        await updateCampaign({ data: payload, organizationId, prescriptionCampaignId }) :
        await createCampaign({ data: payload, organizationId });
      setFormInProgress(false);
      const type = !prescriptionCampaignId ? 'create' : 'update';
      if (response?.data) {
        NotificationService(`${type}PrescriptionCampaign`, { status: 200 });
        history.push(`/settings/organization/prescription-notifications/${prescriptionCampaignId || response.data.id}`);
      } else {
        NotificationService(`${type}PrescriptionCampaign`, response?.error);
      }
    }
  };

  async function handleConfirmDeleteRequest() {
    const response = await deleteCampaign({ organizationId, prescriptionCampaignId });
    if (response?.data) {
      NotificationService('deletePrescriptionCampaign', { status: 200 });
      history.push('/settings/organization/prescription-notifications');
    } else {
      NotificationService('deletePrescriptionCampaign', response?.error);
    }
  }

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <div className="app-page__container" ref={pageContainer}>
      <div className="app-page__container__inner appointment-reminder-form">
        <div className="app-page__header">
          <div className="app-page__header__title">
            <BackButton navigateTo="/settings/organization/prescription-notifications" />
            {`${prescriptionCampaignId ? 'Edit' : 'Create'} Prescription Notification`}
          </div>
        </div>
        <div className="box">
          <div className="box__title-wrapper">
            <div className="box__title">Prescription Notification Details</div>
          </div>
          <div className="appointment-reminder-form__box-wrapper">
            <FormLabel
              id="name"
              className="variable-message__label u-m-t u-m-b-small"
              required
            >
              Name
            </FormLabel>
            <Input
              name="name"
              initialValue={formInputs.name}
              validationMessage={errors?.name}
              className="u-m-b"
              onChange={handleChange}
              explanationMessage="Prescription notification name must be unique."
              dataFeatureTag="prescriptionCampaignName"
              required
            />
          </div>
        </div>
        <PayorsSearchSelect
          selectedPayors={formInputs.payors}
          handleChange={handleChange}
          prescriptionCampaignId={prescriptionCampaign?.id}
          validationMessage={errors?.payors}
        />
        <div className="box">
          <div className="box__title-wrapper">
            <div className="box__title">Initial Fill Notification Message</div>
            <div className="box__subtitle">Text message sent to the patient when a prescription is initially filled.</div>
          </div>
          <RhinoSwitch
            name="enabled"
            isChecked={formInputs.enabled}
            onChange={handleChange}
            label="Enabled"
            dataFeatureTag="edit-prescription-notification_toggle"
          />
          {formInputs.enabled ? (
            <>
              <FormLabel
                id="renderInitialFillSettings"
                className="variable-message__label u-m-t u-m-b-small"
              >
                Send again after (Hours)
              </FormLabel>
              <div className="u-text-italic u-text-muted">If set, resends the same notification after number of hours specified.</div>
              <Input
                name="resendIntervalHours"
                initialValue={formInputs.resendIntervalHours}
                format={{
                  numeral: true,
                  numeralPositiveOnly: true,
                  numeralDecimalScale: 0,
                }}
                className="am__hours-input u-m-r u-m-b u-inline-block"
                onChange={handleChange}
                dataFeatureTag="resendIntervalHours"
                validationMessage={errors.resendIntervalHours}
              />
              <strong data-cypress="secondSendDeliveryHoursFormatted">
                {DateHelpers.convertHoursToDays(formInputs.resendIntervalHours)}
              </strong>
              <div>
                <VariableMessage
                  appointmentEventTypeId={TYPE_PRESCRIPTION_EVENT_REMINDER}
                  attachments={formInputs.attachments}
                  className="u-m-t-large"
                  composeLabel="Message"
                  customWarningMessage={PRESCRIPTION_FILE_UPLOAD_INFO}
                  data-cypress="prescriptionCampaignTemplate"
                  handleTemplateChange={handleChange}
                  initialValue={formInputs.initialValue}
                  name="template"
                  preventFocusOnLoad
                  required
                  template={formInputs.template}
                  validationMessage={errors?.template}
                  variableOptions={PRESCRIPTION_MESSAGE_VARIABLE_OPTIONS}
                />
              </div>
            </>
          ) : null}
        </div>
        <div className="box">
          <div className="box__title-wrapper">
            <div className="box__title">Refill Notification Message</div>
            <div className="box__subtitle">Text message sent to patient when a prescription is due for refill.</div>
          </div>
          <RhinoSwitch
            name="refillTemplateEnabled"
            isChecked={formInputs.refillTemplateEnabled}
            onChange={handleChange}
            label="Enabled"
            dataFeatureTag="edit-prescription-refill-notification_toggle"
          />
          {formInputs.refillTemplateEnabled ? (
            <>
              <FormLabel
                id="renderPrescriptionReminderSettings"
                className="variable-message__label u-m-t u-m-b-small"
              >
                Send prior to refill due (Hours)
              </FormLabel>
              <div className="u-text-italic u-text-muted">Specifies how many hours prior to the refill due date to send a notification. If not set, or a refill due date cannot be determined, the notification will go out immediately.</div>
              <Input
                name="refillDeliveryHours"
                initialValue={formInputs.refillDeliveryHours}
                format={{
                  numeral: true,
                  numeralPositiveOnly: true,
                  numeralDecimalScale: 0,
                }}
                className="am__hours-input u-m-r u-m-b u-inline-block"
                onChange={handleChange}
                dataFeatureTag="refillDeliveryHours"
                validationMessage={errors.refillDeliveryHours}
              />
              <strong data-cypress="refillDeliveryHoursFormatted">
                {DateHelpers.convertHoursToDays(formInputs.refillDeliveryHours)}
              </strong>
              <div>
                <VariableMessage
                  appointmentEventTypeId={TYPE_PRESCRIPTION_EVENT_REMINDER}
                  attachments={formInputs.attachments}
                  className="u-m-t-large"
                  composeLabel="Message"
                  customWarningMessage={PRESCRIPTION_FILE_UPLOAD_INFO}
                  data-cypress="prescriptionCampaignRefillTemplate"
                  handleTemplateChange={handleChange}
                  initialValue={formInputs.initialRefillValue}
                  name="refillTemplate"
                  preventFocusOnLoad
                  required
                  template={formInputs.refillTemplate}
                  validationMessage={errors?.refillTemplate}
                  variableOptions={PRESCRIPTION_MESSAGE_VARIABLE_OPTIONS}
                />
              </div>
            </>
          ) : null}
        </div>

        <div className="u-m-t-large">
          <FormActionButtons
            formInProgress={formInProgress}
            handleConfirmDeleteRequest={handleConfirmDeleteRequest}
            handleSubmit={handleSubmit}
            name="Notification"
            shortName="Notification"
            deletePermissions={[AUTOMATED_MESSAGE_DELETE]}
            editPermissions={[AUTOMATED_MESSAGE_EDIT]}
            dataFeatureTag="prescription-notification_"
            editMode={!!prescriptionCampaignId}
          />
        </div>
      </div>
    </div>
  );
};

export default PrescriptionCampaignForm;
